import React, { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const TRUNCATE_TRIGGER = 60; // chars shown in trigger/badge
const TRUNCATE_ITEM = 80;   // chars shown in dropdown items

function truncate(text, max) {
  if (!text || text.length <= max) return text;
  return text.substring(0, max) + ' [...]';
}

export default function SearchableTagSelect({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  emptyMessage = "No items found.",
  disabled = false,
  className,
  labelKey = 'label',
  valueKey = 'value',
  multiple = false,
  renderItem = null,
}) {
  const [open, setOpen] = useState(false);

  const selectedOptions = multiple
    ? options.filter((option) => Array.isArray(value) && value.includes(option[valueKey]))
    : null;
  const selectedOption = !multiple ? options.find((option) => option[valueKey] === value) : null;

  const handleSelect = (selectedValue) => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      if (arr.includes(selectedValue)) {
        onValueChange(arr.filter((val) => val !== selectedValue));
      } else {
        onValueChange([...arr, selectedValue]);
      }
    } else {
      onValueChange(selectedValue);
      setOpen(false);
    }
  };

  const handleRemove = (e, itemValue) => {
    e.stopPropagation();
    const arr = Array.isArray(value) ? value : [];
    onValueChange(arr.filter((val) => val !== itemValue));
  };

  const isChecked = (optionValue) => {
    if (multiple) {
      return Array.isArray(value) && value.includes(optionValue);
    }
    return value === optionValue;
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between bg-slate-950 border-slate-700 text-slate-100 hover:bg-slate-900 hover:text-slate-100 overflow-hidden max-w-full min-w-0",
              className,
              multiple && selectedOptions && selectedOptions.length > 0 && "h-auto min-h-[36px] py-1.5"
            )}
            disabled={disabled}
          >
            {multiple && selectedOptions && selectedOptions.length > 0 ? (
              <div className="flex flex-wrap gap-1 w-full justify-start min-w-0 overflow-hidden">
                {selectedOptions.map((option) => (
                  <Tooltip key={option[valueKey]}>
                    <TooltipTrigger asChild>
                      <Badge variant="secondary" className="text-xs max-w-full shrink-0">
                        <span className="truncate max-w-[160px] sm:max-w-[200px] inline-block align-middle">
                          {truncate(option[labelKey], TRUNCATE_TRIGGER)}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => handleRemove(e, option[valueKey])}
                          className="ml-1 hover:text-red-400 flex-shrink-0"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    </TooltipTrigger>
                    {option[labelKey]?.length > TRUNCATE_TRIGGER && (
                      <TooltipContent side="top" className="max-w-[300px] text-xs break-words">
                        {option[labelKey]}
                      </TooltipContent>
                    )}
                  </Tooltip>
                ))}
              </div>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate block min-w-0 max-w-[calc(100%-24px)]">
                    {selectedOption ? truncate(selectedOption[labelKey], window.innerWidth < 640 ? 35 : TRUNCATE_TRIGGER) : placeholder}
                  </span>
                </TooltipTrigger>
                {selectedOption && selectedOption[labelKey]?.length > TRUNCATE_TRIGGER && (
                  <TooltipContent side="top" className="max-w-[300px] text-xs break-words">
                    {selectedOption[labelKey]}
                  </TooltipContent>
                )}
              </Tooltip>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-32px)] p-0 bg-slate-900 border-slate-800"
          align="start"
          sideOffset={4}
          style={{ maxHeight: 'min(50vh, 320px)' }}
        >
          <Command className="bg-slate-900">
            <CommandInput 
              placeholder={`Search ${placeholder.toLowerCase()}...`} 
              className="h-9 border-none text-slate-100"
            />
            <CommandList className="max-h-[min(45vh,280px)] overflow-y-auto overscroll-contain touch-pan-y">
              <CommandEmpty className="text-slate-400 text-sm py-6 text-center">
                {emptyMessage}
              </CommandEmpty>
              <CommandGroup className="text-slate-100">
                {options.map((option) => (
                  <Tooltip key={option[valueKey]}>
                    <TooltipTrigger asChild>
                      <CommandItem
                        value={option[labelKey]}
                        onSelect={() => handleSelect(option[valueKey])}
                        className="text-slate-100 aria-selected:bg-slate-800 aria-selected:text-slate-100 cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 flex-shrink-0",
                            isChecked(option[valueKey]) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {renderItem
                          ? <span className="min-w-0 break-words line-clamp-2">{renderItem({ ...option, [labelKey]: truncate(option[labelKey], TRUNCATE_ITEM) })}</span>
                          : <span className="min-w-0 break-words line-clamp-2">{truncate(option[labelKey], TRUNCATE_ITEM)}</span>
                        }
                      </CommandItem>
                    </TooltipTrigger>
                    {option[labelKey]?.length > TRUNCATE_ITEM && (
                      <TooltipContent side="right" className="max-w-[280px] text-xs break-words z-[9999]">
                        {option[labelKey]}
                      </TooltipContent>
                    )}
                  </Tooltip>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
}